import { createAsyncThunk } from '@reduxjs/toolkit';
import { graphqlRequest, hasGraphQLNetworkError } from '~/adapters/graphqlRequest';
import {
  AddToDoNotContactListDocument,
  AddToDoNotContactListMutationResult,
  GetLifecycleSurveyRecipientsDocument,
  InputAddToDoNotContactList,
  InputLifecycleSurveyRecipients,
  UpdateLifecycleSurveyRecipientsDocument,
  UpdateLifecycleSurveyRecipientsMutationResult,
} from '~/gql/graphql';

const getLifecycleSurveyRecipients = createAsyncThunk(
  'mailingLists/getLifecycleSurveyRecipients',
  async ({ surveyId }: { surveyId?: number }, { rejectWithValue }) => {
    const res = await graphqlRequest<any>(GetLifecycleSurveyRecipientsDocument, {
      surveyId,
    });

    if (hasGraphQLNetworkError(res)) return rejectWithValue(res.error);

    return res.data?.getLifecycleSurveyRecipients;
  },
);

const updateLifecycleSurveyRecipient = createAsyncThunk<any, InputLifecycleSurveyRecipients>(
  'mailingLists/updateLifecycleSurveyRecipient',
  async (
    { id, addToDoNotContact, doNotContactReason, inviteImmediately, isApprovedToAdd, removeFromList },
    { rejectWithValue },
  ) => {
    const res = await graphqlRequest<UpdateLifecycleSurveyRecipientsMutationResult>(
      UpdateLifecycleSurveyRecipientsDocument,
      {
        lifecycleSurveyRecipients: [
          {
            id,
            addToDoNotContact,
            doNotContactReason,
            inviteImmediately,
            isApprovedToAdd,
            removeFromList,
          },
        ],
      },
    );

    if (hasGraphQLNetworkError(res)) return rejectWithValue(res.error);

    return {
      ...res.data?.updateLifecycleSurveyRecipients,
      id,
      addToDoNotContact,
      doNotContactReason,
      inviteImmediately,
      isApprovedToAdd,
      removeFromList,
    };
  },
);

const updateLifecycleSurveyRecipients = createAsyncThunk<any, InputLifecycleSurveyRecipients[]>(
  'mailingLists/updateLifecycleSurveyRecipients',
  async (lifecycleSurveyRecipients, { rejectWithValue }) => {
    const res = await graphqlRequest<UpdateLifecycleSurveyRecipientsMutationResult>(
      UpdateLifecycleSurveyRecipientsDocument,
      {
        lifecycleSurveyRecipients,
      },
    );

    if (hasGraphQLNetworkError(res)) return rejectWithValue(res.error);

    return {
      lifecycleSurveyRecipients,
    };
  },
);

const addToDoNotContactList = createAsyncThunk<any, InputAddToDoNotContactList[]>(
  'mailingLists/addToDoNotContactList',
  async (employeesInput: InputAddToDoNotContactList[], { rejectWithValue }) => {
    const res = await graphqlRequest<AddToDoNotContactListMutationResult>(AddToDoNotContactListDocument, {
      employeesInput,
    });

    if (hasGraphQLNetworkError(res)) return rejectWithValue(res.error);

    return {
      ...res.data?.addToDoNotContactList,
    };
  },
);

const approveAllLifecycleSurveyRecipients = createAsyncThunk<
  any,
  { recipientsToApprove: { id: number; isApprovedToAdd: boolean }[] }
>('mailingLists/approveAllLifecycleSurveyRecipients', async ({ recipientsToApprove }, { rejectWithValue }) => {
  const res = await graphqlRequest<UpdateLifecycleSurveyRecipientsMutationResult>(
    UpdateLifecycleSurveyRecipientsDocument,
    {
      lifecycleSurveyRecipients: [...recipientsToApprove],
    },
  );

  if (hasGraphQLNetworkError(res)) return rejectWithValue(res.error);

  return { ...res.data?.updateLifecycleSurveyRecipients, recipientsToApprove };
});

export const lifecycleSurveyRecipientsActions = {
  getLifecycleSurveyRecipients,
  updateLifecycleSurveyRecipient,
  updateLifecycleSurveyRecipients,
  addToDoNotContactList,
  approveAllLifecycleSurveyRecipients,
};
